/**
 * The morph transition between two different looks: the outgoing picture dissolves into a few
 * thousand glowing points, which travel to matching places in the incoming picture and condense
 * into it. Points are sampled where each picture is bright, and paired by (sliced) optimal
 * transport, so each shape goes to the nearest shapes of the new look rather than anywhere.
 */
import {
  bindTex,
  buildProgram,
  uniform,
  type GL,
  type Program,
  type RenderTarget,
  type TargetPool,
} from './gl/gl';
import { UNIT_INPUT } from './frame';

/** Points per morph. */
export const MORPH_POINTS = 8000;
/** Width the pictures are read back at to choose points. */
const SAMPLE_WIDTH = 256;

export interface PointSet {
  /** x, y pairs in 0..1 (y up, like texture coordinates). */
  pos: Float32Array;
  /** r, g, b per point: the picture's hue there, scaled by how bright it is. */
  col: Float32Array;
}

/**
 * Choose `n` points from an RGBA8 picture (rows bottom-up), more where it's brighter, spread
 * evenly (systematic sampling) and jittered within their pixel.
 */
export function samplePoints(
  rgba: Uint8Array,
  w: number,
  h: number,
  n: number,
  rand: () => number,
): PointSet {
  const count = w * h;
  const weight = new Float32Array(count);
  let total = 0;
  for (let i = 0; i < count; i++) {
    const r = rgba[i * 4]! / 255;
    const g = rgba[i * 4 + 1]! / 255;
    const b = rgba[i * 4 + 2]! / 255;
    const l = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    weight[i] = Math.pow(l, 1.5) + 0.002;
    total += weight[i]!;
  }
  const pos = new Float32Array(n * 2);
  const col = new Float32Array(n * 3);
  const step = total / n;
  let next = rand() * step;
  let acc = 0;
  let k = 0;
  for (let i = 0; i < count && k < n; i++) {
    acc += weight[i]!;
    while (acc > next && k < n) {
      const x = i % w;
      const y = Math.floor(i / w);
      pos[k * 2] = (x + rand()) / w;
      pos[k * 2 + 1] = (y + rand()) / h;
      const r = rgba[i * 4]! / 255;
      const g = rgba[i * 4 + 1]! / 255;
      const b = rgba[i * 4 + 2]! / 255;
      const m = Math.max(r, g, b, 1e-3);
      const l = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      // Hue at full strength, dimmed in dark places (density already carries the brightness).
      const gain = Math.min(1, l * 4) * 0.9;
      col[k * 3] = (r / m) * gain;
      col[k * 3 + 1] = (g / m) * gain;
      col[k * 3 + 2] = (b / m) * gain;
      k++;
      next += step;
    }
  }
  for (; k < n; k++) {
    // Rounding left a few unplaced: put them on random points already chosen.
    const j = Math.floor(rand() * Math.max(1, k));
    pos[k * 2] = pos[j * 2]!;
    pos[k * 2 + 1] = pos[j * 2 + 1]!;
    col.copyWithin(k * 3, j * 3, j * 3 + 3);
  }
  return { pos, col };
}

/**
 * Move `from` onto the distribution of `to` by sliced optimal transport: along many random
 * directions, sort both sets and shift each point to its rank-matched partner. Distances are
 * measured with the picture's aspect (`aspect` = width / height). Returns where each `from` point
 * ends up.
 */
export function slicedTransport(
  from: Float32Array,
  to: Float32Array,
  aspect: number,
  iterations: number,
  rand: () => number,
): Float32Array {
  const n = from.length / 2;
  const z = Float32Array.from(from);
  const pz = new Float32Array(n);
  const py = new Float32Array(n);
  // Sort keys: the projection quantized to 18 bits, with the point's index in the low 13 bits,
  // so a plain numeric sort of a Uint32Array (fast, native) gives the order and the indices.
  if (n > 8192) throw new Error('too many points');
  const kz = new Uint32Array(n);
  const ky = new Uint32Array(n);
  const key = (v: number, i: number) =>
    ((Math.min(262143, Math.max(0, Math.floor((v + 4) * 32768))) << 13) | i) >>> 0;
  for (let it = 0; it < iterations; it++) {
    const a = rand() * Math.PI;
    const dx = Math.cos(a) * aspect;
    const dy = Math.sin(a);
    const len2 = dx * dx + dy * dy;
    for (let i = 0; i < n; i++) {
      pz[i] = z[i * 2]! * dx + z[i * 2 + 1]! * dy;
      py[i] = to[i * 2]! * dx + to[i * 2 + 1]! * dy;
      kz[i] = key(pz[i]!, i);
      ky[i] = key(py[i]!, i);
    }
    kz.sort();
    ky.sort();
    for (let k = 0; k < n; k++) {
      const i = kz[k]! & 8191;
      const j = ky[k]! & 8191;
      const shift = (py[j]! - pz[i]!) / len2;
      z[i * 2] = z[i * 2]! + shift * dx;
      z[i * 2 + 1] = z[i * 2 + 1]! + shift * dy;
    }
  }
  return z;
}

const VS = `#version 300 es
in vec2 aFrom;
in vec2 aTo;
in vec3 aColA;
in vec3 aColB;
in float aSeed;
uniform float uP;
uniform float uSize;
out vec3 vCol;
out float vAlpha;
void main() {
  // Each point sets off and arrives at its own moment, so the movement ripples.
  float t = smoothstep(0.12 + 0.18 * aSeed, 0.72 + 0.16 * aSeed, uP);
  vec2 d = aTo - aFrom;
  vec2 side = vec2(-d.y, d.x);
  vec2 pos = mix(aFrom, aTo, t) + side * sin(t * 3.14159) * (aSeed - 0.5) * 0.35;
  gl_Position = vec4(pos * 2.0 - 1.0, 0.0, 1.0);
  gl_PointSize = uSize * (1.0 + 0.5 * sin(t * 3.14159));
  vCol = mix(aColA, aColB, t);
  vAlpha = smoothstep(0.0, 0.18, uP) * (1.0 - smoothstep(0.82, 1.0, uP));
}`;

const FS = `#version 300 es
precision highp float;
in vec3 vCol;
in float vAlpha;
out vec4 fragColor;
void main() {
  float d = length(gl_PointCoord - 0.5) * 2.0;
  float a = exp(-d * d * 4.5) * vAlpha;
  fragColor = vec4(vCol * a, a);
}`;

interface ParticleSet {
  vao: WebGLVertexArrayObject;
  vbo: WebGLBuffer;
  count: number;
  used: boolean;
}

/** Floats per point: from (2), to (2), colour from (3), colour to (3), seed (1). */
const STRIDE = 11;

export class MorphParticles {
  private readonly program: Program;
  private readonly sets = new Map<object, ParticleSet>();

  constructor(
    private readonly gl: GL,
    private readonly pool: TargetPool,
    private readonly blit: Program,
    private readonly vao: () => WebGLVertexArrayObject | null,
  ) {
    this.program = buildProgram(gl, VS, FS);
  }

  /**
   * Draw the points for the morph identified by `key` (its transition or take) at progress `p`
   * over `target`, choosing and pairing them from `a` and `b` the first time.
   */
  draw(key: object, a: RenderTarget, b: RenderTarget, target: RenderTarget, p: number): void {
    const gl = this.gl;
    let set = this.sets.get(key);
    if (!set) {
      set = this.prepare(a, b);
      this.sets.set(key, set);
    }
    set.used = true;
    gl.bindFramebuffer(gl.FRAMEBUFFER, target.fbo);
    gl.viewport(0, 0, target.width, target.height);
    gl.useProgram(this.program.program);
    gl.uniform1f(uniform(gl, this.program, 'uP'), p);
    const cell = Math.sqrt((target.width * target.height) / set.count);
    gl.uniform1f(uniform(gl, this.program, 'uSize'), Math.min(40, Math.max(2, cell * 1.05)));
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.bindVertexArray(set.vao);
    gl.drawArrays(gl.POINTS, 0, set.count);
    gl.disable(gl.BLEND);
    gl.bindVertexArray(this.vao());
  }

  /** After each frame: let go of morphs that have finished. */
  endFrame(): void {
    for (const [key, set] of this.sets) {
      if (set.used) set.used = false;
      else {
        this.gl.deleteBuffer(set.vbo);
        this.gl.deleteVertexArray(set.vao);
        this.sets.delete(key);
      }
    }
  }

  dispose(): void {
    for (const set of this.sets.values()) {
      this.gl.deleteBuffer(set.vbo);
      this.gl.deleteVertexArray(set.vao);
    }
    this.sets.clear();
    this.gl.deleteProgram(this.program.program);
  }

  private prepare(a: RenderTarget, b: RenderTarget): ParticleSet {
    const gl = this.gl;
    const sw = SAMPLE_WIDTH;
    const sh = Math.max(1, Math.round((SAMPLE_WIDTH * a.height) / a.width));
    let seed = 1;
    const rand = () => {
      seed = (seed * 16807) % 2147483647;
      return (seed - 1) / 2147483646;
    };
    const from = samplePoints(this.readSmall(a, sw, sh), sw, sh, MORPH_POINTS, rand);
    const toPixels = this.readSmall(b, sw, sh);
    const target = samplePoints(toPixels, sw, sh, MORPH_POINTS, rand);
    const end = slicedTransport(from.pos, target.pos, a.width / a.height, 40, rand);

    const data = new Float32Array(MORPH_POINTS * STRIDE);
    for (let i = 0; i < MORPH_POINTS; i++) {
      const o = i * STRIDE;
      data[o] = from.pos[i * 2]!;
      data[o + 1] = from.pos[i * 2 + 1]!;
      const ex = Math.min(1, Math.max(0, end[i * 2]!));
      const ey = Math.min(1, Math.max(0, end[i * 2 + 1]!));
      data[o + 2] = ex;
      data[o + 3] = ey;
      data[o + 4] = from.col[i * 3]!;
      data[o + 5] = from.col[i * 3 + 1]!;
      data[o + 6] = from.col[i * 3 + 2]!;
      // The colour where it lands in the new picture.
      const px = Math.min(sw - 1, Math.floor(ex * sw));
      const py = Math.min(sh - 1, Math.floor(ey * sh));
      const j = (py * sw + px) * 4;
      const r = toPixels[j]! / 255;
      const g = toPixels[j + 1]! / 255;
      const bl = toPixels[j + 2]! / 255;
      const m = Math.max(r, g, bl, 1e-3);
      const gain = Math.min(1, (0.2126 * r + 0.7152 * g + 0.0722 * bl) * 4) * 0.9;
      data[o + 7] = (r / m) * gain;
      data[o + 8] = (g / m) * gain;
      data[o + 9] = (bl / m) * gain;
      data[o + 10] = rand();
    }

    const vao = gl.createVertexArray()!;
    const vbo = gl.createBuffer()!;
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
    const attr = (name: string, size: number, offset: number) => {
      const loc = gl.getAttribLocation(this.program.program, name);
      if (loc < 0) return;
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, size, gl.FLOAT, false, STRIDE * 4, offset * 4);
    };
    attr('aFrom', 2, 0);
    attr('aTo', 2, 2);
    attr('aColA', 3, 4);
    attr('aColB', 3, 7);
    attr('aSeed', 1, 10);
    gl.bindVertexArray(this.vao());
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    return { vao, vbo, count: MORPH_POINTS, used: false };
  }

  /** A picture shrunk (by halving, so it's properly averaged) to sw × sh, read back. */
  private readSmall(src: RenderTarget, sw: number, sh: number): Uint8Array {
    const gl = this.gl;
    gl.bindVertexArray(this.vao());
    gl.useProgram(this.blit.program);
    gl.uniform1f(uniform(gl, this.blit, 'uGain'), 1);
    let cur = src;
    let owned: RenderTarget | null = null;
    while (cur.width / 2 >= sw * 1.2) {
      const next = this.pool.acquire(Math.max(1, cur.width >> 1), Math.max(1, cur.height >> 1));
      this.copy(cur, next);
      if (owned) this.pool.release(owned);
      owned = next;
      cur = next;
    }
    const small = this.pool.acquire(sw, sh);
    this.copy(cur, small);
    if (owned) this.pool.release(owned);
    const out = new Uint8Array(sw * sh * 4);
    gl.bindFramebuffer(gl.FRAMEBUFFER, small.fbo);
    gl.readPixels(0, 0, sw, sh, gl.RGBA, gl.UNSIGNED_BYTE, out);
    this.pool.release(small);
    return out;
  }

  private copy(src: RenderTarget, dst: RenderTarget): void {
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, dst.fbo);
    gl.viewport(0, 0, dst.width, dst.height);
    bindTex(gl, this.blit, 'uTex', UNIT_INPUT, src.texture);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }
}
